import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
    GET_EXTERNAL_STATION_SORTING_CODES,
    GetExternalStationSortingCodesResponse,
} from '../../bags/graphql/queries/getExternalStationSortingCodes';
import {
    GET_TT_LOCATION_OPTIONS,
    GetTtLocationOptionsResponse,
    TT_LOCATION_TYPE,
} from '../../bags/graphql/queries/getTtLocationOptions';
import { ExternalStationType } from '../../../../enums/TmsEnum';
import { TEMU_CARRIER_DHLE, TEMU_CARRIER_USPS } from '../types/ChuteEnums';

export interface TemuSortingCodeOptions {
    /** USPS + GA 的目的地候选（后端已合并 TEMU_HANDOVER 同尾程商站点） */
    gaDestinations: string[];
    /** USPS + SCF 的目的地候选（同上合并 HANDOVER，故含 LA） */
    scfDestinations: string[];
    /** DHLe 的单段目的地候选（TEMU_HANDOVER + DHLe） */
    dhleDestinations: string[];
    /** SCF 第四段大箱码候选（tt_location_config TEMU_LARGE_BOX） */
    largeBoxCodes: string[];
    loading: boolean;
}

/**
 * 分拣方案配置页 TEMU 分拣规则四段下拉的候选集，与 PC 建 Temu 包牌表单同一数据源。
 * 页面级只拉一次供所有 TEMU 行共用；没有 TEMU 行时不发请求。
 */
export const useTemuSortingCodeOptions = (enabled: boolean): TemuSortingCodeOptions => {
    const { data: gaData, loading: gaLoading } = useQuery<GetExternalStationSortingCodesResponse>(
        GET_EXTERNAL_STATION_SORTING_CODES,
        { variables: { stationType: ExternalStationType.TEMU_GA, carrierCode: TEMU_CARRIER_USPS }, skip: !enabled }
    );
    const { data: scfData, loading: scfLoading } = useQuery<GetExternalStationSortingCodesResponse>(
        GET_EXTERNAL_STATION_SORTING_CODES,
        { variables: { stationType: ExternalStationType.TEMU_SCF, carrierCode: TEMU_CARRIER_USPS }, skip: !enabled }
    );
    const { data: dhleData, loading: dhleLoading } = useQuery<GetExternalStationSortingCodesResponse>(
        GET_EXTERNAL_STATION_SORTING_CODES,
        { variables: { stationType: ExternalStationType.TEMU_HANDOVER, carrierCode: TEMU_CARRIER_DHLE }, skip: !enabled }
    );
    const { data: largeBoxData, loading: largeBoxLoading } = useQuery<GetTtLocationOptionsResponse>(
        GET_TT_LOCATION_OPTIONS,
        { variables: { locationType: TT_LOCATION_TYPE.TEMU_LARGE_BOX }, skip: !enabled }
    );

    return useMemo(() => ({
        gaDestinations: gaData?.getExternalStationSortingCodes ?? [],
        scfDestinations: scfData?.getExternalStationSortingCodes ?? [],
        dhleDestinations: dhleData?.getExternalStationSortingCodes ?? [],
        largeBoxCodes: (largeBoxData?.getTtLocationOptions ?? []).map(o => o.code),
        loading: gaLoading || scfLoading || dhleLoading || largeBoxLoading,
    }), [gaData, scfData, dhleData, largeBoxData, gaLoading, scfLoading, dhleLoading, largeBoxLoading]);
};
